import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpRightFromSquare, faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { library as fontAwesomeLibrary } from '@fortawesome/fontawesome-svg-core';
import { FtpInstructionsModal } from './ftpinstructionsmodal.js';
import { ApiInstructionsModal } from './apiinstructionsmodal.js';
import { HeliocloudS3InstructionsModal } from './helioclouds3instructionsmodal.js';
import { HELIOCLOUDS3LINK } from './../constants.js';
import { sendEventToGA } from './../utils.js';
 
fontAwesomeLibrary.add(faUpRightFromSquare);
fontAwesomeLibrary.add(faCircleInfo);
 
/**
 * Build JSX rows for each data access method.
 *
 * @param {object} params
 * @param {object} params.datasetData       - Response from backend describing dataset.
 * @param {object} params.isModalOpenArray  - Current modal open state map.
 * @param {string} params.hcutId            - Dataset ID.
 * @param {string} [params.modalId]         - ID of modal to mark open initially.
 * @param {Function} params.onFtpClick      - Handler for FTP row clicks.
 * @param {Function} params.onS3Click       - Handler for S3 row clicks.
 * @param {Function} params.onApiClick      - Handler for "More API Options" click.
 * @returns {{ ftpRows, heliocloudS3Rows, httpRows, isModalOpen, url, httpLastLink, ftpLastLink }}
 */
export function getDataAccessRows({ datasetData, isModalOpenArray, hcutId, modalId = null, onFtpClick, onS3Click, onApiClick }) {
  const numFtpUrls = datasetData.dataAccessMethods
    .filter(m => m.url.toLowerCase().startsWith('ftp'))
    .length;
 
  const numHeliocloudS3Urls = datasetData.dataAccessMethods
    .filter(m => m.url.toLowerCase().startsWith(HELIOCLOUDS3LINK))
    .length;
 
  const ftpRows = [];
  const httpRows = [];
  const heliocloudS3Rows = [];
  let isModalOpen = { ...isModalOpenArray };
  let url, httpLastLink, ftpLastLink;
 
  datasetData.dataAccessMethods.forEach(accessMethod => {
    const rawUrl = accessMethod.url;
    const lowerUrl = rawUrl.toLowerCase();
    let onClick, label, labelHostname, icon;
    let isFTP = false;
    let isHeliocloudS3 = false;
 
    const mouseDownHandler = (event) => {
      if (event.button === 1) {
        window.open(rawUrl, '_blank');
        window.focus();
      }
    };
 
    if (lowerUrl.startsWith('ftp')) {
      isFTP = true;
      url = rawUrl;
      ftpLastLink = rawUrl;
 
      if (modalId && rawUrl === modalId) {
        isModalOpen = { ...isModalOpen, [rawUrl]: true };
      }
 
      onClick = () => onFtpClick(rawUrl);
      label = numFtpUrls === 1
        ? 'FTP Download'
        : <span>FTP Download</span>;
      labelHostname = numFtpUrls > 1
        ? <span>({new URL(rawUrl.replace('ftp', 'http')).hostname})</span>
        : null;
      icon = <FontAwesomeIcon icon={faCircleInfo} />;
 
    } else if (lowerUrl.startsWith(HELIOCLOUDS3LINK)) {
      isHeliocloudS3 = true;
      url = rawUrl;
 
      if (modalId && rawUrl === modalId) {
        isModalOpen = { ...isModalOpen, [rawUrl]: true };
      }
 
      onClick = () => onS3Click(rawUrl);
      label = numHeliocloudS3Urls === 1
        ? 'S3 Download'
        : <span>S3 Download</span>;
      labelHostname = numHeliocloudS3Urls > 1
        ? <span>(HelioCloud)</span>
        : null;
      icon = <FontAwesomeIcon icon={faCircleInfo} />;
 
    } else {
      httpLastLink = rawUrl;
      onClick = () => {
        sendEventToGA('search_result__search_result_table_http_pill__button', 'http', hcutId);
        window.open(rawUrl, '_blank');
        window.focus();
      };
      label = accessMethod.name;
      icon = <FontAwesomeIcon icon={faUpRightFromSquare} />;
    }
 
    if (isFTP) {
      ftpRows.push(
        <div key={rawUrl} className="dropdownItem dropdownContentNarrow" onClick={onClick}>
          <div className="dropdownPillTopRow">
            <div
              className="dropdownPillTopRowLeft"
              onMouseDown={mouseDownHandler}
              title={accessMethod.altText}>
              {label}&nbsp;<span>{icon}</span>
            </div>
          </div>
          {labelHostname && <div onClick={onClick}>{labelHostname}</div>}
        </div>
      );
    } else if (isHeliocloudS3) {
      heliocloudS3Rows.push(
        <div key={rawUrl} className="dropdownItem dropdownContentNarrow" onClick={onClick}>
          <div className="dropdownPillTopRow">
            <div
              className="dropdownPillTopRowLeft"
              onMouseDown={mouseDownHandler}
              title={accessMethod.altText}>
              {label}&nbsp;<span>{icon}</span>
            </div>
          </div>
          {labelHostname && <div onClick={onClick}>{labelHostname}</div>}
        </div>
      );
    } else {
      httpRows.push(
        <div
          key={rawUrl}
          className="dropdownItem dropdownContentWide"
          onMouseDown={mouseDownHandler}
          onClick={onClick}
          title={accessMethod.altText}>
          {label}&nbsp;<span>{icon}</span>
        </div>
      );
    }
  });
 
  // "More API Options" entry appended to S3 rows when HTTP methods exist
  const hasHttpMethods = datasetData.dataAccessMethods.some(
    m => !m.url.toLowerCase().startsWith('ftp') && !m.url.toLowerCase().startsWith(HELIOCLOUDS3LINK)
  );
  if (hasHttpMethods) {
    heliocloudS3Rows.push(
      <div
        key="more-api-options"
        className="dropdownItem dropdownContentNarrow"
        onClick={() => onApiClick()}>
        <div className="dropdownPillTopRow">
          <div className="dropdownPillTopRowLeft">
            More API Options&nbsp;<span><FontAwesomeIcon icon={faCircleInfo} /></span>
          </div>
        </div>
      </div>
    );
  }
 
  return { ftpRows, heliocloudS3Rows, httpRows, isModalOpen, url, httpLastLink, ftpLastLink };
}
 
 
/**
 * Build FTP modal JSX elements.
 */
export function getDataAccessFtpModals(datasetData, isFtpModalOpen, setFtpModalOpen) {
  return datasetData.dataAccessMethods
    .map(m => m.url)
    .filter(url => url.toLowerCase().startsWith('ftp'))
    .map(ftpUrl => (
      <FtpInstructionsModal
        key={ftpUrl}
        ftpUrl={ftpUrl}
        isFtpModalOpen={isFtpModalOpen[ftpUrl]}
        setFtpModalOpen={setFtpModalOpen}
      />
    ));
}
 
 
/**
 * Build API modal JSX element.
 */
export function getDataAccessApiModals(datasetData, isApiModalOpen, setApiModalOpen) {
  return [
    <ApiInstructionsModal
      key="api-modal"
      apiDataset={datasetData}
      isApiModalOpen={isApiModalOpen}
      setApiModalOpen={setApiModalOpen}
    />
  ];
}
 
 
/**
 * Build HelioCloud S3 modal JSX elements.
 */
export function getDataAccessHeliocloudS3Modals(datasetData, isHeliocloudS3ModalOpen, setHeliocloudS3ModalOpen) {
  return datasetData.dataAccessMethods
    .map(m => m.url)
    .filter(url => url.toLowerCase().startsWith(HELIOCLOUDS3LINK))
    .map((hcS3Url, i) => (
      <HeliocloudS3InstructionsModal
        key={i + 3000}
        heliocloudS3ProductUrl={hcS3Url}
        productKey={datasetData.cdasId}
        isHeliocloudS3ModalOpen={isHeliocloudS3ModalOpen[hcS3Url]}
        setHeliocloudS3ModalOpen={setHeliocloudS3ModalOpen}
      />
    ));
}
